
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  toRefs,
  watch,
} from "vue";
import gsap from "gsap";

interface Item {
  key: number;
  value: unknown;
}

interface LocalState {
  visibleItems: Item[];
  rowsOffset: number;
  rowOffset: number;
  capacity: number;
  itemHeight: number;
  scrollHeight: number;
  scrollTop: number;
  listEl: HTMLDivElement | null;
  scrollEl: HTMLDivElement | null;
  container: HTMLDivElement | null;
  listStyle: Record<string, string>;
}
export default defineComponent({
  name: "VirtualScroller",
  props: {
    items: {
      type: Array as () => (Item | string)[],
      default: () => [],
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
    value: {
      type: [Number, String],
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const state: LocalState = reactive({
      rowsOffset: computed(() =>
        state.itemHeight ? Math.floor(state.scrollTop / state.itemHeight) : 0
      ),
      rowOffset: computed(() =>
        state.itemHeight
          ? state.scrollTop - state.rowsOffset * state.itemHeight
          : 0
      ),
      itemHeight: 0,
      scrollTop: 0,
      scrollHeight: computed(() => props.items.length * state.itemHeight),
      capacity: computed(() =>
        state.itemHeight
          ? Math.floor(props.maxHeight / state.itemHeight) + 1
          : 0
      ),
      visibleItems: computed(() =>
        props.items
          .map((item, key) =>
            typeof item === "string"
              ? {
                  value: item,
                  key,
                }
              : item
          )
          .slice(state.rowsOffset, state.capacity + state.rowsOffset)
      ),
      listEl: null,
      scrollEl: null,
      container: null,
      listStyle: computed(() => ({
        marginTop: `-${
          state.scrollHeight -
          state.scrollTop +
          state.rowOffset -
          Math.min(state.visibleItems.length, 4) -
          1
        }px`,
      })),
    });
    watch(
      () => !!(state.container && state.itemHeight && props.isOpen),
      (val) => {
        if (val) {
          const index = props.items
            .map((item) => (typeof item === "string" ? item : item.value))
            .findIndex((val) => val === props.value);
          if (index > -1) {
            gsap.to(state.container, {
              scrollTo: Math.max(index - 2.7, 0) * state.itemHeight,
              duration: 0.2,
            });
          }
        }
      }
    );
    watch(
      () => props.isOpen && props.value,
      (val) => {
        if (!val) {
          requestAnimationFrame(() => {
            state.scrollTop = 0;
          });
        }
      }
    );
    onMounted(() => {
      const sizer = document.createElement("div");
      sizer.innerText = "0";
      sizer.classList.add("list-group-item", "list-group-item-action");
      if (state.listEl instanceof HTMLDivElement) {
        state.listEl.appendChild(sizer);
        requestAnimationFrame(() => {
          state.itemHeight = sizer.offsetHeight + 1;
          sizer.remove();
        });
      }
    });
    const onScroll = (e: Event) => {
      if (e.target instanceof HTMLDivElement) {
        state.scrollTop = e.target.scrollTop;
      }
    };
    return {
      ...toRefs(state),
      onScroll,
    };
  },
});
